'use client';

import { useAuthStore } from '../store/auth-store';
import { Button } from '../components/ui/button';
import { Loader2, LogOut, Plus, Store, ChevronRight, AlertCircle, PackageCheck, ShieldAlert, Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useRouter } from 'next/navigation';

interface ShopSummary {
  shops: {
    id: number;
    name: string;
    slug: string;
    is_active: boolean;
    logo_url?: string;
    // SHOP_OWNER | SHOP_STAFF | WAREHOUSE_MANAGER | ACCOUNTANT (WP-0.1 tagság)
    membership_role?: string;
  }[];
  plan: {
    name: string | null;
    max_shops: number;
    used_shops: number;
    remaining_shops: number;
    can_create: boolean;
  };
  user: {
    first_name: string | null;
    last_name: string | null;
    email: string;
  };
}

export default function Home() {
  const { user, logout, _hasHydrated } = useAuthStore();
  const router = useRouter();

  const { data, isLoading, error } = useQuery<ShopSummary>({
    queryKey: ['shops-summary'],
    queryFn: async () => {
      const r = await api.get('/shops/me/summary');
      return r.data;
    },
    enabled: _hasHydrated && !!user,
  });

  const handleLogout = () => {
    logout();
    router.push('/login');
  };

  if (!_hasHydrated) {
    return (
      <div className="flex h-screen items-center justify-center bg-za-bg">
        <Loader2 className="h-8 w-8 animate-spin text-za-mid" />
      </div>
    );
  }

  const plan = data?.plan;
  const shops = data?.shops ?? [];
  const displayName = data?.user?.first_name || user?.first_name || user?.email?.split('@')[0] || 'Felhasználó';

  const limitPercent = plan && plan.max_shops > 0
    ? Math.round((plan.used_shops / plan.max_shops) * 100)
    : 0;
  const limitWarning = plan && plan.remaining_shops === 0 && plan.max_shops > 0;
  const canCreate = plan?.can_create ?? true;
  // Munkatársi (nem tulajdonosi) hozzáférés jelvénye a bolt-kártyán
  const ROLE_BADGE: Record<string, string> = {
    SHOP_STAFF: 'Munkatárs',
    WAREHOUSE_MANAGER: 'Raktáros',
    ACCOUNTANT: 'Könyvelő',
  };

  return (
    <main className="min-h-screen bg-za-bg px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-4">

        {/* ── Fejléc ── */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-za-surface border border-za-border px-3 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 bg-za-dark flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-black tracking-widest">Z</span>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-za-dark">Üdvözöljük, {displayName}!</h1>
              <p className="text-xs text-za-mid mt-0.5">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2 self-start sm:self-auto text-za-mid border-za-border hover:bg-za-hover hover:text-za-dark">
            <LogOut className="h-4 w-4" />
            Kijelentkezés
          </Button>
        </header>

        {/* ── Csomag összefoglaló ── */}
        {plan && (
          <div className={`flex flex-col sm:flex-row sm:items-center gap-4 border px-3 py-3 sm:px-5 sm:py-4 ${limitWarning ? 'bg-orange-50 border-za-warning/30' : 'bg-za-surface border-za-border'}`}>
            <div className="flex items-start gap-3 flex-1">
              {limitWarning
                ? <ShieldAlert className="h-5 w-5 text-za-warning mt-0.5 shrink-0" />
                : <PackageCheck className="h-5 w-5 text-za-mid mt-0.5 shrink-0" />
              }
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-za-dark">
                  {plan.name ? `${plan.name} csomag` : 'Ingyenes csomag'}
                </p>
                <p className="text-xs text-za-mid mt-0.5">
                  {plan.used_shops} / {plan.max_shops} bolt használatban
                  {plan.remaining_shops > 0 && ` · még ${plan.remaining_shops} hozható létre`}
                  {limitWarning && ' · elérte a csomaghatárt'}
                </p>
                {plan.max_shops > 0 && (
                  <div className="mt-2 h-1 w-full bg-za-border overflow-hidden">
                    <div
                      className={`h-full transition-all ${limitWarning ? 'bg-za-warning' : 'bg-za-mid'}`}
                      style={{ width: `${limitPercent}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
            {!plan.can_create && (
              <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-za-mid border border-dashed border-za-border px-3 py-1.5 cursor-default select-none" title="A csomagbővítés hamarosan elérhető lesz">
                <PackageCheck className="h-3.5 w-3.5" />
                Csomag bővítése – hamarosan
              </span>
            )}
          </div>
        )}

        {/* ── Bolt lista ── */}
        <section className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-za-mid uppercase tracking-wider">Boltjaim</h2>
            {canCreate && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="gap-1.5 border-za-border text-za-dark hover:bg-za-hover text-xs" onClick={() => router.push('/shops/new')}>
                  <Plus className="h-3.5 w-3.5" />
                  Új bolt
                </Button>
                <button
                  onClick={() => router.push('/shops/wizard2')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5,#0891b2)', boxShadow: '0 0 14px rgba(124,58,237,0.45)' }}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Varázsolj egy boltot ✦
                </button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-za-mid" />
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 bg-orange-50 border border-za-warning/30 text-za-warning text-sm px-4 py-3">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Nem sikerült betölteni a boltokat. Próbáld újra.
            </div>
          ) : shops.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 border-2 border-dashed border-za-border py-14 text-center bg-za-surface">
              <div className="bg-za-hover p-4">
                <Store className="h-7 w-7 text-za-mid" />
              </div>
              <div>
                <p className="font-semibold text-za-dark">Még nincs boltod</p>
                <p className="text-sm text-za-mid mt-1">
                  {canCreate
                    ? 'Hozd létre az első boltodat, és kezdj el értékesíteni!'
                    : 'A csomagod jelenleg nem tesz lehetővé új bolt létrehozását.'}
                </p>
              </div>
              {canCreate ? (
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <Button variant="outline" onClick={() => router.push('/shops/new')} className="gap-2 border-za-border text-za-dark hover:bg-za-hover">
                    <Plus className="h-4 w-4" />
                    Üres bolt
                  </Button>
                  <button
                    onClick={() => router.push('/shops/wizard2')}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5,#0891b2)', boxShadow: '0 0 18px rgba(124,58,237,0.45)' }}
                  >
                    <Sparkles className="h-4 w-4" />
                    Varázsolj egy boltot ✦
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2.5">
                  <div className="flex flex-col sm:flex-row items-center gap-2 opacity-40 pointer-events-none select-none" aria-disabled="true">
                    <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-za-dark border border-za-border">
                      <Plus className="h-4 w-4" />
                      Üres bolt
                    </span>
                    <span
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded"
                      style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5,#0891b2)' }}
                    >
                      <Sparkles className="h-4 w-4" />
                      Varázsolj egy boltot ✦
                    </span>
                  </div>
                  <p className="inline-flex items-center gap-1.5 text-xs font-medium text-za-mid">
                    <Sparkles className="h-3.5 w-3.5" />
                    Több bolt létrehozása hamarosan
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {shops.map((shop) => (
                <button
                  key={shop.id}
                  onClick={() => router.push(`/shops/${shop.id}/dashboard`)}
                  // min-w-0: grid-elem alapból min-width:auto → a hosszú domain
                  // (pl. villamossagi-termekek-webaruhaza.zava.hu) szétfeszítené az
                  // oszlopot és az egész lap vízszintesen elcsúszna.
                  className="group flex items-center gap-3 sm:gap-4 min-w-0 bg-za-surface border border-za-border px-3 py-3 sm:px-4 sm:py-4 hover:bg-za-hover hover:border-za-border-mid transition-colors text-left w-full"
                >
                  {/* Ikon / Logo */}
                  <div className="shrink-0 w-10 h-10 bg-za-hover border border-za-border flex items-center justify-center overflow-hidden">
                    {shop.logo_url
                      ? <img src={shop.logo_url} alt={shop.name} className="w-full h-full object-cover" />
                      : <Store className="h-4 w-4 text-za-mid" />
                    }
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-za-dark truncate text-sm">{shop.name}</p>
                    <p className="text-xs text-za-mid truncate mt-0.5">{shop.slug}.zava.hu</p>
                  </div>

                  {/* Státusz + nyíl */}
                  <div className="flex items-center gap-2 shrink-0">
                    {shop.membership_role && ROLE_BADGE[shop.membership_role] && (
                      <span className="text-xs px-2 py-0.5 font-medium border border-za-gold bg-[#fdf5e8] text-za-deep">
                        {ROLE_BADGE[shop.membership_role]}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 font-medium ${shop.is_active ? 'bg-za-hover text-za-dark border border-za-border' : 'bg-za-bg text-za-mid border border-za-border'}`}>
                      {shop.is_active ? 'Aktív' : 'Inaktív'}
                    </span>
                    <ChevronRight className="h-4 w-4 text-za-border group-hover:text-za-mid transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

      </div>
    </main>
  );
}
